import { when } from 'mobx';
import { useCallback, useEffect, useId, useState } from 'react';

import { useStore } from '@feathr/extender/state';

/**
 * Return type for the useSubscriptionsFilter hook.
 */
export interface IUseSubscriptionsFilterReturn {
  /** Whether to show only projects with email history */
  showOnlyEmailedProjects: boolean;
  /** Set of project IDs that have email delivery history */
  projectIdsWithEmailHistory: Set<string>;
  /** Whether the email history data is still loading */
  isLoadingEmailHistory: boolean;
  /** Function to toggle the filter state */
  toggleFilter: () => void;
}

/**
 * Hook that manages email history filtering for person subscriptions.
 *
 * Fetches breadcrumbs filtered by pinpoint_tracked_email_delivered flavor
 * to determine which projects a person has received emails from.
 *
 * @param personId - The ID of the person to fetch email history for
 * @returns Filter state and controls
 *
 * @example
 * const {
 *   showOnlyEmailedProjects,
 *   projectIdsWithEmailHistory,
 *   isLoadingEmailHistory,
 *   toggleFilter,
 * } = useSubscriptionsFilter(person.id);
 */
export function useSubscriptionsFilter(personId: string): IUseSubscriptionsFilterReturn {
  const { Breadcrumbs } = useStore();
  const [showOnlyEmailedProjects, setShowOnlyEmailedProjects] = useState(true);
  const [projectIdsWithEmailHistory, setProjectIdsWithEmailHistory] = useState<Set<string>>(
    new Set(),
  );
  const [isLoadingEmailHistory, setIsLoadingEmailHistory] = useState(true);
  const listId = useId();

  useEffect(() => {
    async function fetchEmailHistory(): Promise<void> {
      setIsLoadingEmailHistory(true);
      try {
        const breadcrumbs = Breadcrumbs.list(
          {
            predicates: [
              {
                kind: 'activity',
                attr_against: 'per_id',
                attr_type: 'string',
                comparison: 'eq',
                value: personId,
              },
              {
                kind: 'activity',
                attr_against: 'flvr',
                attr_type: 'string',
                comparison: 'eq',
                value: 'pinpoint_tracked_email_delivered',
              },
            ],
            mode: 'match_all',
            lookback_mode: 'unbounded',
            lookback_value: 1,
            only: ['e_id'],
            pagination: { page_size: 1000 },
          },
          {},
          listId,
        );

        await when(() => breadcrumbs.isLoaded);

        const projectIds = new Set<string>();
        breadcrumbs.models.forEach((breadcrumb) => {
          const eventId = breadcrumb.get('e_id');
          if (eventId) {
            projectIds.add(eventId);
          }
        });

        setProjectIdsWithEmailHistory(projectIds);
      } catch {
        setShowOnlyEmailedProjects(false);
        setProjectIdsWithEmailHistory(new Set());
      } finally {
        setIsLoadingEmailHistory(false);
      }
    }

    fetchEmailHistory();
  }, [Breadcrumbs, personId, listId]);

  const toggleFilter = useCallback(() => {
    setShowOnlyEmailedProjects((prev) => !prev);
  }, []);

  return {
    showOnlyEmailedProjects,
    projectIdsWithEmailHistory,
    isLoadingEmailHistory,
    toggleFilter,
  };
}
